import { useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon, BoltIcon, CircleStackIcon, CpuChipIcon, GlobeAltIcon, HomeIcon, PlayCircleIcon, UsersIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { text } from './components/DataTable'
import { OverviewPage } from './pages/OverviewPage'
import { RecordsPage } from './pages/RecordsPage'
import { emptyPayload, isPayload, type DataSection, type Payload, type RecordRow, type Section } from './types'

// Production uses the same-origin Pages Function proxy. This preserves the
// Cloudflare Access assertion while avoiding a second Access login boundary.
const API = import.meta.env.VITE_API_URL || ''
const nav: Array<{ id: Section; label: string; icon: typeof HomeIcon }> = [
  { id: 'overview', label: 'Overview', icon: HomeIcon }, { id: 'loops', label: 'Loops', icon: ArrowPathIcon },
  { id: 'runs', label: 'Loop runs', icon: PlayCircleIcon }, { id: 'engines', label: 'Engines', icon: BoltIcon },
  { id: 'models', label: 'Models', icon: CpuChipIcon }, { id: 'users', label: 'Users', icon: UsersIcon },
  { id: 'sites', label: 'Websites', icon: GlobeAltIcon },
]

const sectionFromPath = (): Section => {
  const candidate = window.location.pathname.split('/').filter(Boolean)[0] as Section | undefined
  return nav.some(item => item.id === candidate) ? candidate! : 'overview'
}
const pathForSection = (section: Section) => section === 'overview' ? '/' : `/${section}`

function App() {
  const [section, setSection] = useState<Section>(sectionFromPath)
  const [data, setData] = useState<Payload>(emptyPayload)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<RecordRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const response = await fetch(`${API}/api/admin/system-control`, { credentials: 'include' })
      if (!response.ok) {
        const message = response.status === 401
          ? 'Phiên đăng nhập không hợp lệ. Hãy đăng nhập lại vào system.conan.school.'
          : response.status === 403
            ? 'Tài khoản này không có quyền System Admin.'
            : `Không thể tải dữ liệu (${response.status}).`
        throw new Error(message)
      }
      const body = await response.json() as { data?: unknown }
      if (!isPayload(body.data)) throw new Error('API trả về dữ liệu không đúng định dạng.')
      setData({ ...body.data, warnings: body.data.warnings || [] })
    } catch (loadError) { setError(loadError instanceof Error ? loadError.message : 'Không thể kết nối api.conan.school.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const handlePopState = () => { setSection(sectionFromPath()); setSelected(null) }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const navigate = (nextSection: Section) => {
    if (nextSection !== section) window.history.pushState({}, '', pathForSection(nextSection))
    setSection(nextSection); setSelected(null)
  }
  const current = nav.find(item => item.id === section) || nav[0]

  return <div className="app">
    <aside><div className="brand"><div className="brand-mark" aria-hidden="true">C</div><div><b>CONAN SYSTEM</b><span>Control plane</span></div></div><nav>{nav.map(item => <a key={item.id} href={pathForSection(item.id)} className={section === item.id ? 'active' : ''} onClick={event => { event.preventDefault(); navigate(item.id) }}><item.icon/><span>{item.label}</span>{item.id !== 'overview' && <em>{data[item.id].length}</em>}</a>)}</nav><div className="aside-foot"><CircleStackIcon/><div><b>api.conan.school</b><span className={error ? 'connection-error' : ''}>{loading ? 'Connecting…' : error ? 'Disconnected' : data.warnings.length ? 'Degraded' : 'Connected'}</span></div></div></aside>
    <main><header><div><p>Sư Tử Con Operations</p><h1>{current.label}</h1></div><button className="refresh" onClick={load} disabled={loading}><ArrowPathIcon className={loading ? 'spin' : ''}/>Làm mới</button></header>
      {error ? <div className="notice"><b>Không thể mở control plane</b><p>{error}</p><button onClick={load}>Thử lại</button></div> : section === 'overview' ? <OverviewPage data={data} loading={loading} navigate={navigate}/> : <RecordsPage key={section} section={section as DataSection} title={current.label} rows={data[section as DataSection]} loading={loading} onSelect={setSelected}/>}</main>
    {selected && <Drawer row={selected} onClose={() => setSelected(null)}/>} 
  </div>
}

function Drawer({ row, onClose }: { row: RecordRow; onClose: () => void }) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose])
  return <div className="drawer-backdrop" onClick={onClose}><aside className="drawer" role="dialog" aria-modal="true" aria-label="Chi tiết bản ghi" onClick={event => event.stopPropagation()}><div className="drawer-head"><div><p>Record detail</p><h2>{text(row.name || row.loop || row.email || row.id)}</h2></div><button aria-label="Đóng" onClick={onClose} autoFocus><XMarkIcon/></button></div><dl>{Object.entries(row).map(([key,value]) => <div key={key}><dt>{key.replaceAll('_',' ')}</dt><dd>{text(value)}</dd></div>)}</dl></aside></div>
}

export default App
